import { createSlice } from '@reduxjs/toolkit';
import { AutomationStatusEnum } from '../../enums/AutomationStatusEnum';
import {
    WORKFLOW_MODE,
    RUN_STATUS,
    RIGHT_PANEL_VIEW,
    REPLAY_STEP_STATUS,
} from './constants';
import {
    reconcileSelection,
    getFlatSteps,
    getActiveFlatSteps,
    buildStepIndexToStepId,
    findStepById,
    maskSavedPlanRuntimeFields,
    shouldResetExecutionSessionOnSave,
    mapReplayCompleteStatus,
    resolveStepIdFromReplayIndex,
    allStepIds,
    getStepIdsBetween,
    reindexPlanStepIndices,
    clonePlanSnapshot,
    captureLeftPanelGroupSnapshot,
    hasGeneratedSteps,
    hasGenerationSessionArtifacts,
} from './planUtils';
import {
    collectTestStepIndexKeys,
    coalesceExecutionStrategies,
} from './executionStrategiesUtils';
import {
    captureInstructionHeadingsSnapshot,
    rebuildInstructionHeadingsSnapshot,
} from './instructionHeadingsSnapshotUtils';
import { reconcileRecordingPlanInstructions, normalizeInstructionsRecordingMarkers } from './recordingPlanMarkers';
import { buildClearedInstructions } from './planUtils';
import { getChangeTrackingBaselineInstructions } from './changeTrackingUtils';

const GENERATION_COMPLETABLE_STATUSES = [
    RUN_STATUS.RUNNING,
    RUN_STATUS.STOPPED,
    RUN_STATUS.FAILED,
];

function canAcceptGenerationCompletion(state) {
    return GENERATION_COMPLETABLE_STATUSES.includes(state.generationRun?.status);
}

function setLeftPanelGroupSnapshot(state) {
    state.planDraft.leftPanelGroupSnapshotInstructions = captureLeftPanelGroupSnapshot(
        state.planDraft.instructions
    );
}

function clearLeftPanelGroupSnapshot(state) {
    state.planDraft.leftPanelGroupSnapshotInstructions = null;
}

function markPlanDraftSaveableAfterSession(state) {
    if (
        !hasGenerationSessionArtifacts({
            generationRun: state.generationRun,
            planDraft: state.planDraft,
        })
    ) {
        return;
    }
    state.planDraft.instructionsChangedSinceSteps = false;
    state.planDraft.isDirty = true;
    state.savedPlan.isSaved = false;
    // Treat post-generate plan as clean for highlights — dirty only after user edits.
    setChangeTrackingBaseline(
        state,
        state.planDraft.instructions,
        state.planDraft.dynamicResult
    );
}

const emptyGenerationRun = () => ({
    runId: null,
    status: RUN_STATUS.IDLE,
    stoppedByUser: false,
    sessionMode: null,
    logs: [],
    hierarchicalSteps: null,
    executionFlow: null,
    testStepsByExecutionId: {},
    screenshots: [],
    tokenUsage: null,
    result: null,
    error: null,
    completedInstructions: null,
});

const emptyReplayRun = () => ({
    runId: null,
    status: RUN_STATUS.IDLE,
    stepIndexToStepId: {},
    instructionsSnapshot: [],
    currentStepId: null,
    progressByStepId: {},
    resultsByStepId: {},
    logs: [],
    summary: null,
    error: null,
});

export const initialState = {
    workflow: {
        mode: WORKFLOW_MODE.EMPTY,
        error: null,
    },
    planDraft: {
        instructions: [],
        dynamicResult: null,
        isDirty: false,
        validationErrorsByStepId: {},
        source: 'empty',
        instructionHeadingsSnapshot: {},
        /** True after post-recording save so execution UI stays visible until reload/reset. */
        keepRecordingExecutionUi: false,
        /** True after post-recording save; skips API reload and keeps session execution chrome. */
        justSavedInSession: false,
        /** Instruction text edited after steps exist; disables replay until regenerate/save. */
        instructionsChangedSinceSteps: false,
        /** Frozen instruction groups for left panel after load/save; 0-step groups stay fixed until generate. */
        leftPanelGroupSnapshotInstructions: null,
        /** Baseline instructions for dirty highlights when no lastSavedSnapshot yet. */
        changeTrackingBaseline: null,
    },
    generationRun: emptyGenerationRun(),
    replayRun: emptyReplayRun(),
    savedPlan: {
        testCaseId: null,
        testCaseKey: null,
        automationStatus: null,
        isSaved: false,
        isLoading: false,
        lastSavedSnapshot: null,
        /** Saved/API execution record plan — legacy currentPlan equivalent for replay metadata. */
        executionRecordPlan: null,
    },
    selection: {
        selectedStepId: null,
        selectedStepIds: [],
        selectionAnchorId: null,
    },
    ui: {
        rightPanelView: RIGHT_PANEL_VIEW.INSTRUCTIONS,
        hasEnteredInstructionsMode: false,
        selectedEnvironment: null,
        selectedAIModel: null,
        selectedAIModelIsAuto: true,
    },
    fileSuggestions: [],
    fileDescriptions: {},
    mcpToolsList: [],
    mcpToolMap: {},
};

function syncWorkflowAfterPlanChange(state) {
    const hasSteps = state.planDraft.instructions.some(
        (inst) => inst.aiSteps?.length > 0
    );
    const hasText = state.planDraft.instructions.some(
        (inst) => String(inst?.text ?? '').trim().length > 0
    );

    if (state.generationRun.status === RUN_STATUS.RUNNING) {
        state.workflow.mode = WORKFLOW_MODE.GENERATING;
        return;
    }
    if (state.replayRun.status === RUN_STATUS.RUNNING) {
        state.workflow.mode = WORKFLOW_MODE.REPLAYING;
        return;
    }
    if (state.replayRun.status === RUN_STATUS.COMPLETED || state.replayRun.status === RUN_STATUS.FAILED) {
        state.workflow.mode = WORKFLOW_MODE.REVIEWING_RESULTS;
        return;
    }
    if (state.selection.selectedStepId) {
        state.workflow.mode = WORKFLOW_MODE.EDITING_STEP;
        return;
    }
    if (hasSteps) {
        state.workflow.mode = WORKFLOW_MODE.REVIEWING_STEPS;
        return;
    }
    if (hasText || state.ui.hasEnteredInstructionsMode) {
        state.workflow.mode = WORKFLOW_MODE.EDITING_INSTRUCTIONS;
        return;
    }
    state.workflow.mode = WORKFLOW_MODE.EMPTY;
}

function applySelectionReconcile(state) {
    const next = reconcileSelection(state.planDraft.instructions, state.selection);
    state.selection.selectedStepIds = next.selectedStepIds;
    state.selection.selectionAnchorId = next.selectionAnchorId;
    state.selection.selectedStepId = next.selectedStepId;
}

function setChangeTrackingBaseline(state, instructions, dynamicResult) {
    state.planDraft.changeTrackingBaseline = clonePlanSnapshot({
        instructions: instructions ?? [],
        dynamicResult: dynamicResult ?? null,
    }).instructions;
}

function reconcileReplayRunToCurrentPlan(state) {
    if (state.replayRun.status !== RUN_STATUS.RUNNING) return;

    const flatSteps = getActiveFlatSteps(state.planDraft.instructions);
    const validStepIds = new Set(flatSteps.map((step) => step.id).filter(Boolean));
    const stepIndexToStepId = buildStepIndexToStepId(flatSteps);

    state.replayRun.stepIndexToStepId = stepIndexToStepId;
    state.replayRun.progressByStepId = Object.fromEntries(
        Object.entries(state.replayRun.progressByStepId ?? {}).filter(([stepId]) =>
            validStepIds.has(stepId)
        )
    );
    state.replayRun.resultsByStepId = Object.fromEntries(
        Object.entries(state.replayRun.resultsByStepId ?? {}).filter(([stepId]) =>
            validStepIds.has(stepId)
        )
    );

    if (state.replayRun.currentStepId && !validStepIds.has(state.replayRun.currentStepId)) {
        state.replayRun.currentStepId = null;
    }
}

function markNextReplayStepInProgress(state, completedStepIndex) {
    if (state.replayRun.status !== RUN_STATUS.RUNNING) return;
    if (!Number.isFinite(Number(completedStepIndex))) return;

    const nextIndex = Number(completedStepIndex) + 1;
    const nextStepId = resolveStepIdFromReplayIndex(nextIndex, state.replayRun.stepIndexToStepId);
    if (!nextStepId) {
        state.replayRun.currentStepId = null;
        return;
    }

    state.replayRun.currentStepId = nextStepId;
    state.replayRun.progressByStepId[nextStepId] = REPLAY_STEP_STATUS.IN_PROGRESS;
    state.replayRun.resultsByStepId[nextStepId] = {
        ...(state.replayRun.resultsByStepId[nextStepId] ?? {}),
        stepIndex: nextIndex,
        status: 'executing',
    };
}

function clearInProgressReplaySteps(state) {
    Object.entries(state.replayRun.progressByStepId ?? {}).forEach(([stepId, status]) => {
        if (status === REPLAY_STEP_STATUS.IN_PROGRESS) {
            delete state.replayRun.progressByStepId[stepId];
        }
    });
    Object.entries(state.replayRun.resultsByStepId ?? {}).forEach(([stepId, result]) => {
        if (result?.status === 'executing' || result?.status === REPLAY_STEP_STATUS.IN_PROGRESS) {
            delete state.replayRun.resultsByStepId[stepId];
        }
    });
    state.replayRun.currentStepId = null;
}

function markUnfinishedReplayStepsSkipped(state) {
    Object.entries(state.replayRun.stepIndexToStepId ?? {}).forEach(([index, stepId]) => {
        const status = state.replayRun.progressByStepId?.[stepId];
        if (
            status === REPLAY_STEP_STATUS.PASSED ||
            status === REPLAY_STEP_STATUS.FAILED ||
            status === REPLAY_STEP_STATUS.SKIPPED
        ) {
            return;
        }

        state.replayRun.progressByStepId[stepId] = REPLAY_STEP_STATUS.SKIPPED;
        state.replayRun.resultsByStepId[stepId] = {
            ...(state.replayRun.resultsByStepId[stepId] ?? {}),
            stepIndex: Number(index),
            status: REPLAY_STEP_STATUS.SKIPPED,
        };
    });
    state.replayRun.currentStepId = null;
}

export const automationStudioNewSlice = createSlice({
    name: 'automationStudioNew',
    initialState,
    reducers: {
        startAutomation: (state) => {
            state.planDraft.instructions = [{ id: Date.now(), text: '', aiSteps: [] }];
            state.planDraft.instructionHeadingsSnapshot = {};
            state.planDraft.isDirty = false;
            state.planDraft.changeTrackingBaseline = null;
            state.planDraft.source = 'manual';
            state.ui.hasEnteredInstructionsMode = true;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.INSTRUCTIONS;
            state.generationRun = emptyGenerationRun();
            state.replayRun = emptyReplayRun();
            state.workflow.error = null;
            syncWorkflowAfterPlanChange(state);
        },

        setInstructions: (state, action) => {
            const prevInstructions = state.planDraft.instructions;
            state.planDraft.instructions = action.payload;
            if (
                hasGeneratedSteps(prevInstructions) &&
                state.generationRun.status !== RUN_STATUS.RUNNING
            ) {
                state.planDraft.instructionsChangedSinceSteps = true;
            }
            applySelectionReconcile(state);
            syncWorkflowAfterPlanChange(state);
        },

        // Plan-structure updates (paste/reorder) — do not mark instruction-text drift.
        replacePlanInstructions: (state, action) => {
            state.planDraft.instructions = action.payload;
            applySelectionReconcile(state);
            syncWorkflowAfterPlanChange(state);
        },

        clearInstructionEditorText: (state) => {
            state.planDraft.instructions = buildClearedInstructions(state.planDraft.instructions);
            applySelectionReconcile(state);
            syncWorkflowAfterPlanChange(state);
        },

        resetInstructionTextFromBaseline: (state) => {
            const baseline = getChangeTrackingBaselineInstructions({
                savedPlan: state.savedPlan,
                planDraft: state.planDraft,
                generationRun: state.generationRun,
            });
            if (!baseline?.length) return;

            const baselineById = new Map(
                baseline.filter((inst) => inst?.id != null).map((inst) => [String(inst.id), inst])
            );
            const next = state.planDraft.instructions
                .filter((inst) => inst?.id != null && baselineById.has(String(inst.id)))
                .map((inst) => ({
                    ...inst,
                    text: String(baselineById.get(String(inst.id)).text ?? ''),
                    removedFromEditor: false,
                }));

            state.planDraft.instructions = next.length
                ? next
                : [{ id: Date.now(), text: '', aiSteps: [] }];
            // Instruction text matches highlight baseline again; do not touch isDirty/isSaved
            // (those track save/replay vs lastSavedSnapshot, not highlight chrome).
            state.planDraft.instructionsChangedSinceSteps = false;
            applySelectionReconcile(state);
            syncWorkflowAfterPlanChange(state);
        },

        markInstructionsChanged: (state) => {
            state.planDraft.instructionsChangedSinceSteps = true;
            state.replayRun = emptyReplayRun();
        },

        updateStepInPlan: (state, action) => {
            const step = action.payload;
            state.planDraft.instructions = state.planDraft.instructions.map((inst) => ({
                ...inst,
                aiSteps: inst.aiSteps?.map((s) => (s.id === step.id ? step : s)),
            }));
            state.planDraft.isDirty = true;
            state.savedPlan.isSaved = false;
            if (state.selection.selectedStepId === step.id) {
                state.selection.selectedStepId = step.id;
            }
            syncWorkflowAfterPlanChange(state);
        },

        loadPlanFromTestcase: (state, action) => {
            const {
                instructions,
                dynamicResult,
                testCaseId,
                testCaseKey,
                executionRecordPlan,
                automationStatus,
            } = action.payload;
            state.planDraft.instructions = maskSavedPlanRuntimeFields(instructions ?? []);
            state.planDraft.instructionHeadingsSnapshot = rebuildInstructionHeadingsSnapshot(
                state.planDraft.instructions
            );
            state.planDraft.dynamicResult = dynamicResult ?? null;
            state.planDraft.isDirty = false;
            state.planDraft.instructionsChangedSinceSteps = false;
            state.planDraft.source = 'savedTestcase';
            state.planDraft.keepRecordingExecutionUi = false;
            state.planDraft.justSavedInSession = false;
            state.planDraft.validationErrorsByStepId = {};
            state.savedPlan.testCaseId = testCaseId ?? null;
            state.savedPlan.testCaseKey = testCaseKey ?? null;
            state.savedPlan.automationStatus = automationStatus ?? null;
            state.savedPlan.isSaved = true;
            state.savedPlan.isLoading = false;
            state.savedPlan.lastSavedSnapshot = clonePlanSnapshot({
                instructions: state.planDraft.instructions,
                dynamicResult: state.planDraft.dynamicResult,
            });
            setChangeTrackingBaseline(
                state,
                state.planDraft.instructions,
                state.planDraft.dynamicResult
            );
            state.savedPlan.executionRecordPlan = executionRecordPlan ?? null;
            state.generationRun = emptyGenerationRun();
            state.replayRun = emptyReplayRun();
            state.ui.hasEnteredInstructionsMode = true;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.INSTRUCTIONS;
            state.selection.selectedStepId = null;
            state.selection.selectedStepIds = [];
            state.selection.selectionAnchorId = null;
            applySelectionReconcile(state);
            syncWorkflowAfterPlanChange(state);
            setLeftPanelGroupSnapshot(state);
        },

        setSavedPlanLoading: (state, action) => {
            state.savedPlan.isLoading = action.payload;
        },

        setValidationErrors: (state, action) => {
            state.planDraft.validationErrorsByStepId = action.payload ?? {};
        },

        generationStarted: (state, action) => {
            const runId = action.payload?.runId ?? `gen-${Date.now()}`;
            const sessionMode = action.payload?.sessionMode ?? null;
            clearLeftPanelGroupSnapshot(state);
            state.planDraft.instructionHeadingsSnapshot = {};
            state.planDraft.keepRecordingExecutionUi = false;
            state.planDraft.justSavedInSession = false;
            state.planDraft.instructionsChangedSinceSteps = false;
            state.generationRun = {
                ...emptyGenerationRun(),
                runId,
                status: RUN_STATUS.RUNNING,
                sessionMode,
            };
            state.replayRun = emptyReplayRun();
            state.workflow.error = null;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.EXECUTION;
            state.workflow.mode = WORKFLOW_MODE.GENERATING;
        },

        generationLogReceived: (state, action) => {
            if (state.generationRun.status !== RUN_STATUS.RUNNING) return;
            const raw = action.payload;
            const entry =
                typeof raw === 'string'
                    ? { message: raw, timestamp: Date.now() }
                    : {
                          message: raw?.message ?? String(raw ?? ''),
                          timestamp: raw?.timestamp ?? Date.now(),
                          level: raw?.level,
                      };
            state.generationRun.logs.push(entry);
            if (state.generationRun.logs.length > 1000) {
                state.generationRun.logs = state.generationRun.logs.slice(-1000);
            }
        },

        generationHierarchicalReceived: (state, action) => {
            if (state.generationRun.status !== RUN_STATUS.RUNNING) return;
            state.generationRun.hierarchicalSteps = action.payload;
        },

        generationExecutionFlowReceived: (state, action) => {
            if (state.generationRun.status !== RUN_STATUS.RUNNING) return;
            state.generationRun.executionFlow = action.payload;
        },

        generationScreenshotReceived: (state, action) => {
            if (state.generationRun.status !== RUN_STATUS.RUNNING) return;
            state.generationRun.screenshots.push(action.payload);
            if (state.generationRun.screenshots.length > 50) {
                state.generationRun.screenshots = state.generationRun.screenshots.slice(-50);
            }
        },

        generationTestStepReceived: (state, action) => {
            if (state.generationRun.status !== RUN_STATUS.RUNNING) return;
            const raw = action.payload?.step ?? action.payload?.testStep ?? action.payload;
            if (!raw || typeof raw !== 'object') return;
            const strategies = coalesceExecutionStrategies(raw);
            const step = strategies ? { ...raw, executionStrategies: strategies } : raw;
            const keys = collectTestStepIndexKeys(step);
            const keysToWrite = keys.length > 0 ? keys : [`step-${state.generationRun.logs.length}`];
            keysToWrite.forEach((key) => {
                state.generationRun.testStepsByExecutionId[key] = step;
            });
        },

        generationTokenUsageReceived: (state, action) => {
            if (state.generationRun.status !== RUN_STATUS.RUNNING) return;
            state.generationRun.tokenUsage = action.payload;
        },

        generationCompleted: (state, action) => {
            if (!canAcceptGenerationCompletion(state)) return;

            const { instructions, dynamicResult, result, executionRecordPlan } = action.payload ?? {};
            state.generationRun.status = state.generationRun.stoppedByUser
                ? RUN_STATUS.STOPPED
                : RUN_STATUS.COMPLETED;
            state.generationRun.result = result ?? null;
            state.generationRun.error = null;
            state.workflow.error = null;
            if (instructions) {
                const finalInstructions = reconcileRecordingPlanInstructions(
                    instructions,
                    state.planDraft.instructions
                );
                state.planDraft.instructions = finalInstructions;
                state.planDraft.source = 'generated';
                state.planDraft.instructionHeadingsSnapshot = captureInstructionHeadingsSnapshot(
                    finalInstructions,
                    state.planDraft.instructionHeadingsSnapshot
                );
                state.generationRun.completedInstructions = clonePlanSnapshot({
                    instructions: finalInstructions,
                }).instructions;
            }
            if (dynamicResult !== undefined) {
                state.planDraft.dynamicResult = dynamicResult;
            }
            if (executionRecordPlan) {
                state.savedPlan.executionRecordPlan = executionRecordPlan;
            }
            state.planDraft.instructionsChangedSinceSteps = false;
            state.savedPlan.isSaved = false;
            // Generated plan is not a user edit — reset highlight baseline to current plan.
            setChangeTrackingBaseline(
                state,
                state.planDraft.instructions,
                state.planDraft.dynamicResult
            );
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.EXECUTION;
            applySelectionReconcile(state);
            syncWorkflowAfterPlanChange(state);
        },

        generationFailed: (state, action) => {
            state.generationRun.status = RUN_STATUS.FAILED;
            state.generationRun.error = action.payload ?? 'Generation failed';
            state.workflow.mode = WORKFLOW_MODE.ERROR;
            state.workflow.error = state.generationRun.error;
            markPlanDraftSaveableAfterSession(state);
        },

        generationStopped: (state) => {
            state.generationRun.status = RUN_STATUS.STOPPED;
            state.generationRun.stoppedByUser = true;
            const hasPartialSession = hasGenerationSessionArtifacts({
                generationRun: state.generationRun,
                planDraft: state.planDraft,
            });
            if (hasPartialSession) {
                markPlanDraftSaveableAfterSession(state);
            }
            state.ui.rightPanelView = hasPartialSession
                ? RIGHT_PANEL_VIEW.EXECUTION
                : RIGHT_PANEL_VIEW.INSTRUCTIONS;
            syncWorkflowAfterPlanChange(state);
        },

        markPlanUnsavedFromGeneration: (state) => {
            state.savedPlan.isSaved = false;
        },

        replayStarted: (state, action) => {
            const runId = action.payload?.runId ?? `replay-${Date.now()}`;
            const flatSteps = getActiveFlatSteps(state.planDraft.instructions);
            const stepIndexToStepId = buildStepIndexToStepId(flatSteps);

            if (state.replayRun.status === RUN_STATUS.RUNNING) {
                if (!action.payload?.runId || state.replayRun.runId === runId) {
                    if (Object.keys(state.replayRun.stepIndexToStepId ?? {}).length === 0) {
                        state.replayRun.stepIndexToStepId = stepIndexToStepId;
                    }
                    return;
                }
            }

            // Preserve left-panel group snapshot across replay (unlike generation).
            state.replayRun = {
                ...emptyReplayRun(),
                runId,
                status: RUN_STATUS.RUNNING,
                stepIndexToStepId,
                instructionsSnapshot: clonePlanSnapshot({
                    instructions: state.planDraft.instructions,
                    dynamicResult: null,
                }).instructions,
            };
            state.workflow.error = null;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.EXECUTION;
            state.workflow.mode = WORKFLOW_MODE.REPLAYING;
        },

        replayStepStarted: (state, action) => {
            const payload = action.payload ?? {};
            const { stepIndex, sessionId, runId } = payload;
            const correlationId = runId ?? sessionId;
            if (correlationId && state.replayRun.runId && correlationId !== state.replayRun.runId) return;
            const stepId = resolveStepIdFromReplayIndex(stepIndex, state.replayRun.stepIndexToStepId);
            if (!stepId) return;
            state.replayRun.currentStepId = stepId;
            state.replayRun.progressByStepId[stepId] = REPLAY_STEP_STATUS.IN_PROGRESS;
            state.replayRun.resultsByStepId[stepId] = {
                ...(state.replayRun.resultsByStepId[stepId] ?? {}),
                ...payload,
                stepIndex,
                sessionId,
                status: 'executing',
            };
            state.replayRun.logs.push({
                message: `\n📍 Starting step ${stepIndex}: ${payload.tool ?? ''}`,
                level: 'info',
                timestamp: Date.now(),
            });
        },

        replayStepCompleted: (state, action) => {
            const payload = action.payload ?? {};
            const { stepIndex, status, screenshot, sessionId, runId } = payload;
            const correlationId = runId ?? sessionId;
            if (correlationId && state.replayRun.runId && correlationId !== state.replayRun.runId) return;
            const stepId = resolveStepIdFromReplayIndex(stepIndex, state.replayRun.stepIndexToStepId);
            if (!stepId) return;
            const mappedStatus = mapReplayCompleteStatus(status);
            const prev = state.replayRun.resultsByStepId[stepId] ?? {};
            const resultWithIndex = { ...payload, stepIndex };
            state.replayRun.progressByStepId[stepId] = mappedStatus;
            state.replayRun.resultsByStepId[stepId] = {
                ...prev,
                stepIndex,
                status: mappedStatus,
                result: resultWithIndex,
                screenshot: screenshot ?? prev.screenshot,
                sessionId: sessionId ?? prev.sessionId,
            };
            if (mappedStatus === REPLAY_STEP_STATUS.PASSED || mappedStatus === REPLAY_STEP_STATUS.SKIPPED) {
                markNextReplayStepInProgress(state, stepIndex);
            } else {
                state.replayRun.currentStepId = null;
                if (mappedStatus === REPLAY_STEP_STATUS.FAILED) {
                    markUnfinishedReplayStepsSkipped(state);
                }
            }
            state.replayRun.logs.push({
                message: `   ✓ Step ${stepIndex} ${mappedStatus}`,
                level: mappedStatus === REPLAY_STEP_STATUS.FAILED ? 'error' : 'success',
                timestamp: Date.now(),
            });
        },

        replayCompleted: (state, action) => {
            clearInProgressReplaySteps(state);
            state.replayRun.status = RUN_STATUS.COMPLETED;
            state.replayRun.summary = action.payload ?? null;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.EXECUTION;
            state.workflow.mode = WORKFLOW_MODE.REVIEWING_RESULTS;
        },

        replayFailed: (state, action) => {
            clearInProgressReplaySteps(state);
            state.replayRun.status = RUN_STATUS.FAILED;
            state.replayRun.error = action.payload?.error ?? action.payload ?? 'Replay failed';
            state.workflow.mode = WORKFLOW_MODE.ERROR;
            state.workflow.error = state.replayRun.error;
        },

        replayStopped: (state) => {
            markUnfinishedReplayStepsSkipped(state);
            state.replayRun.status = RUN_STATUS.STOPPED;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.EXECUTION;
            state.workflow.mode = WORKFLOW_MODE.REVIEWING_RESULTS;
        },

        replayLogReceived: (state, action) => {
            if (state.replayRun.status !== RUN_STATUS.RUNNING) return;
            const raw = action.payload;
            const entry =
                typeof raw === 'string'
                    ? { message: raw, timestamp: Date.now(), level: 'info' }
                    : {
                          message: raw?.message ?? String(raw ?? ''),
                          timestamp: raw?.timestamp ?? Date.now(),
                          level: raw?.level ?? 'info',
                      };
            state.replayRun.logs.push(entry);
            if (state.replayRun.logs.length > 1000) {
                state.replayRun.logs = state.replayRun.logs.slice(-1000);
            }
        },

        planStructureChanged: (state) => {
            state.planDraft.isDirty = true;
            state.savedPlan.isSaved = false;
            reconcileReplayRunToCurrentPlan(state);
        },

        saveStarted: (state) => {
            state.workflow.mode = WORKFLOW_MODE.SAVING;
            state.workflow.error = null;
        },

        planSaved: (state, action) => {
            const { testCaseId, testCaseKey, executionRecordPlan } = action.payload ?? {};
            const hadCompletedRecordingSession = shouldResetExecutionSessionOnSave(
                state.generationRun
            );

            const currentInstructions = clonePlanSnapshot({
                instructions: state.planDraft.instructions,
            }).instructions;

            // Live draft keeps recording execution UI; only normalize replay markers.
            state.planDraft.instructions = normalizeInstructionsRecordingMarkers(
                currentInstructions
            );
            state.planDraft.isDirty = false;
            state.planDraft.instructionsChangedSinceSteps = false;
            state.planDraft.justSavedInSession = hadCompletedRecordingSession;
            state.planDraft.keepRecordingExecutionUi = hadCompletedRecordingSession;

            // Reset/reload baseline: masked like loadPlanFromTestcase (no live execution chrome).
            const snapshotInstructions = maskSavedPlanRuntimeFields(
                normalizeInstructionsRecordingMarkers(currentInstructions)
            );

            state.savedPlan.isSaved = true;
            if (testCaseId) state.savedPlan.testCaseId = testCaseId;
            if (testCaseKey) state.savedPlan.testCaseKey = testCaseKey;
            state.savedPlan.automationStatus = AutomationStatusEnum.NLP_AUTOMATED;
            state.savedPlan.lastSavedSnapshot = clonePlanSnapshot({
                instructions: snapshotInstructions,
                dynamicResult: state.planDraft.dynamicResult,
            });
            setChangeTrackingBaseline(
                state,
                snapshotInstructions,
                state.planDraft.dynamicResult
            );
            if (executionRecordPlan) {
                state.savedPlan.executionRecordPlan = executionRecordPlan;
            }
            state.planDraft.leftPanelGroupSnapshotInstructions = captureLeftPanelGroupSnapshot(
                snapshotInstructions
            );
            if (state.savedPlan.testCaseId) {
                state.workflow.mode = WORKFLOW_MODE.READY_TO_REPLAY;
            } else {
                syncWorkflowAfterPlanChange(state);
            }
        },

        instructionsOnlySaved: (state, action) => {
            const { testCaseId, testCaseKey } = action.payload ?? {};

            state.planDraft.instructions = state.planDraft.instructions.map((inst) => ({
                ...inst,
                aiSteps: [],
            }));
            state.planDraft.dynamicResult = null;
            state.planDraft.isDirty = false;
            state.planDraft.instructionsChangedSinceSteps = false;
            state.planDraft.justSavedInSession = false;
            state.planDraft.keepRecordingExecutionUi = false;

            const snapshotInstructions = maskSavedPlanRuntimeFields(
                normalizeInstructionsRecordingMarkers(
                    clonePlanSnapshot({ instructions: state.planDraft.instructions }).instructions
                )
            );

            state.savedPlan.isSaved = true;
            if (testCaseId) state.savedPlan.testCaseId = testCaseId;
            if (testCaseKey) state.savedPlan.testCaseKey = testCaseKey;
            state.savedPlan.automationStatus = AutomationStatusEnum.NOT_AUTOMATED;
            state.savedPlan.executionRecordPlan = null;
            state.savedPlan.lastSavedSnapshot = clonePlanSnapshot({
                instructions: snapshotInstructions,
                dynamicResult: null,
            });
            setChangeTrackingBaseline(state, snapshotInstructions, null);
            state.planDraft.leftPanelGroupSnapshotInstructions = captureLeftPanelGroupSnapshot(
                snapshotInstructions
            );

            state.generationRun = emptyGenerationRun();
            state.replayRun = emptyReplayRun();
            state.selection.selectedStepId = null;
            state.selection.selectedStepIds = [];
            state.selection.selectionAnchorId = null;
            state.workflow.error = null;
            state.workflow.mode = WORKFLOW_MODE.EDITING_INSTRUCTIONS;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.INSTRUCTIONS;
        },

        selectStep: (state, action) => {
            const stepId = action.payload;
            state.selection.selectedStepId = stepId;
            state.selection.selectedStepIds = stepId ? [stepId] : [];
            state.selection.selectionAnchorId = stepId;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.STEP_EDITOR;
            state.workflow.mode = WORKFLOW_MODE.EDITING_STEP;
        },

        selectStepWithModifier: (state, action) => {
            const { stepId, shiftKey, metaKey, ctrlKey } = action.payload ?? {};
            if (!stepId) return;

            const orderedIds = allStepIds(state.planDraft.instructions);
            if (!orderedIds.includes(stepId)) return;

            const openEditor = () => {
                state.ui.rightPanelView = RIGHT_PANEL_VIEW.STEP_EDITOR;
                state.workflow.mode = WORKFLOW_MODE.EDITING_STEP;
            };

            if (shiftKey && state.selection.selectionAnchorId) {
                state.selection.selectedStepIds = getStepIdsBetween(
                    orderedIds,
                    state.selection.selectionAnchorId,
                    stepId
                );
                state.selection.selectedStepId = stepId;
                openEditor();
                return;
            }

            if (metaKey || ctrlKey) {
                const baseIds =
                    state.selection.selectedStepIds?.length > 0
                        ? state.selection.selectedStepIds
                        : state.selection.selectedStepId
                          ? [state.selection.selectedStepId]
                          : [];
                const next = new Set(baseIds);
                if (next.has(stepId)) {
                    next.delete(stepId);
                } else {
                    next.add(stepId);
                }
                const ids = orderedIds.filter((id) => next.has(id));
                state.selection.selectedStepIds = ids;
                state.selection.selectionAnchorId = stepId;
                state.selection.selectedStepId = ids.length ? stepId : null;
                if (ids.length) {
                    openEditor();
                }
                return;
            }

            state.selection.selectedStepId = stepId;
            state.selection.selectedStepIds = [stepId];
            state.selection.selectionAnchorId = stepId;
            openEditor();
        },

        setSelectionAfterPaste: (state, action) => {
            const ids = action.payload ?? [];
            state.selection.selectedStepIds = [...new Set(ids)];
            state.selection.selectionAnchorId = ids.length ? ids[ids.length - 1] : null;
            const firstId = ids[0];
            if (firstId) {
                const step = findStepById(state.planDraft.instructions, firstId);
                state.selection.selectedStepId = step ? firstId : state.selection.selectedStepId;
            }
            if (state.selection.selectedStepId) {
                state.ui.rightPanelView = RIGHT_PANEL_VIEW.STEP_EDITOR;
                state.workflow.mode = WORKFLOW_MODE.EDITING_STEP;
            }
        },

        addStepAfter: (state, action) => {
            const { instructionId, afterStepIndex, step } = action.payload;
            const inst = state.planDraft.instructions.find((i) => i.id === instructionId);
            if (!inst?.aiSteps?.length || inst.removedFromEditor) return;

            const withInserted = state.planDraft.instructions.map((i) => {
                if (i.id !== instructionId) return i;
                const steps = [...(i.aiSteps ?? [])];
                steps.splice(afterStepIndex + 1, 0, step);
                return { ...i, aiSteps: steps };
            });
            state.planDraft.instructions = reindexPlanStepIndices(withInserted);
            state.planDraft.isDirty = true;
            state.savedPlan.isSaved = false;
            reconcileReplayRunToCurrentPlan(state);
            const inserted = findStepById(state.planDraft.instructions, step.id);
            if (inserted) {
                state.selection.selectedStepId = step.id;
                state.selection.selectedStepIds = [step.id];
                state.selection.selectionAnchorId = step.id;
                state.ui.rightPanelView = RIGHT_PANEL_VIEW.STEP_EDITOR;
                state.workflow.mode = WORKFLOW_MODE.EDITING_STEP;
            }
        },

        deleteStepFromPlan: (state, action) => {
            const stepId = action.payload;
            const withoutStep = state.planDraft.instructions.map((inst) => ({
                ...inst,
                aiSteps: (inst.aiSteps ?? []).filter((s) => s.id !== stepId),
            }));
            state.planDraft.instructions = reindexPlanStepIndices(withoutStep);
            state.planDraft.isDirty = true;
            state.savedPlan.isSaved = false;
            reconcileReplayRunToCurrentPlan(state);
            applySelectionReconcile(state);
            if (state.selection.selectedStepId === stepId) {
                state.selection.selectedStepId = null;
                state.ui.rightPanelView = RIGHT_PANEL_VIEW.INSTRUCTIONS;
            }
            syncWorkflowAfterPlanChange(state);
        },

        selectAdjacentStep: (state, action) => {
            const direction = action.payload;
            const flat = getFlatSteps(state.planDraft.instructions);
            const currentIdx = flat.findIndex((s) => s.id === state.selection.selectedStepId);
            if (currentIdx === -1) return;
            const nextIdx = direction === 'prev' ? currentIdx - 1 : currentIdx + 1;
            if (nextIdx < 0 || nextIdx >= flat.length) return;
            const nextId = flat[nextIdx].id;
            state.selection.selectedStepId = nextId;
            state.selection.selectedStepIds = [nextId];
            state.selection.selectionAnchorId = nextId;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.STEP_EDITOR;
            state.workflow.mode = WORKFLOW_MODE.EDITING_STEP;
        },

        setStreamedInstructions: (state, action) => {
            if (state.generationRun.status !== RUN_STATUS.RUNNING) return;
            state.planDraft.instructions = action.payload;
            state.planDraft.instructionHeadingsSnapshot = captureInstructionHeadingsSnapshot(
                action.payload,
                state.planDraft.instructionHeadingsSnapshot
            );
        },

        clearSelection: (state) => {
            state.selection.selectedStepId = null;
            state.selection.selectedStepIds = [];
            state.selection.selectionAnchorId = null;
            syncWorkflowAfterPlanChange(state);
        },

        navigateToInstructions: (state) => {
            state.selection.selectedStepId = null;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.INSTRUCTIONS;
            syncWorkflowAfterPlanChange(state);
        },

        navigateToExecution: (state) => {
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.EXECUTION;
            syncWorkflowAfterPlanChange(state);
        },

        saveFailed: (state, action) => {
            state.workflow.mode = WORKFLOW_MODE.ERROR;
            state.workflow.error = action.payload ?? 'Save failed';
        },

        setMcpToolsList: (state, action) => {
            state.mcpToolsList = action.payload ?? [];
            state.mcpToolMap = (action.payload ?? []).reduce((acc, tool) => {
                if (!tool?.name) return acc;
                const values = {};
                if (tool?.inputSchema?.properties) {
                    Object.entries(tool.inputSchema.properties).forEach(([key, value]) => {
                        values[key] = {
                            ...value,
                            required:
                                Array.isArray(tool?.inputSchema?.required) &&
                                tool.inputSchema.required.includes(key),
                        };
                    });
                }
                acc[tool.name] = values;
                return acc;
            }, {});
        },

        setFileSuggestions: (state, action) => {
            state.fileSuggestions = action.payload?.suggestions ?? [];
            state.fileDescriptions = action.payload?.descriptions ?? {};
        },

        setSelectedEnvironment: (state, action) => {
            state.ui.selectedEnvironment = action.payload;
        },

        setSelectedAIModel: (state, action) => {
            state.ui.selectedAIModel = action.payload;
        },

        setHasEnteredInstructionsMode: (state, action) => {
            state.ui.hasEnteredInstructionsMode = action.payload;
            syncWorkflowAfterPlanChange(state);
        },

        importAutomationPlan: (state, action) => {
            const { instructions, dynamicResult } = action.payload;
            state.planDraft.instructions = instructions ?? [];
            state.planDraft.instructionHeadingsSnapshot = rebuildInstructionHeadingsSnapshot(
                state.planDraft.instructions
            );
            state.planDraft.dynamicResult = dynamicResult ?? null;
            state.planDraft.isDirty = true;
            state.planDraft.instructionsChangedSinceSteps = false;
            state.planDraft.source = 'import';
            setChangeTrackingBaseline(
                state,
                state.planDraft.instructions,
                state.planDraft.dynamicResult
            );
            state.savedPlan.isSaved = false;
            state.generationRun = emptyGenerationRun();
            state.replayRun = emptyReplayRun();
            state.selection.selectedStepId = null;
            state.selection.selectedStepIds = [];
            state.selection.selectionAnchorId = null;
            state.ui.hasEnteredInstructionsMode = true;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.INSTRUCTIONS;
            applySelectionReconcile(state);
            syncWorkflowAfterPlanChange(state);
        },

        resetPlanToSnapshot: (state) => {
            const snap = state.savedPlan.lastSavedSnapshot;
            if (!snap?.instructions) return;

            const restored = clonePlanSnapshot(snap);
            state.planDraft.instructions = restored.instructions.map((inst) => {
                const { removedFromEditor: _removed, ...clean } = inst;
                return clean;
            });
            state.planDraft.instructionHeadingsSnapshot = rebuildInstructionHeadingsSnapshot(
                state.planDraft.instructions
            );
            state.planDraft.dynamicResult = restored.dynamicResult;
            state.planDraft.isDirty = false;
            state.planDraft.instructionsChangedSinceSteps = false;
            state.planDraft.justSavedInSession = false;
            state.planDraft.keepRecordingExecutionUi = false;
            state.planDraft.validationErrorsByStepId = {};
            setChangeTrackingBaseline(
                state,
                state.planDraft.instructions,
                state.planDraft.dynamicResult
            );
            state.generationRun = emptyGenerationRun();
            state.replayRun = emptyReplayRun();
            state.workflow.error = null;
            state.selection.selectedStepId = null;
            state.selection.selectedStepIds = [];
            state.selection.selectionAnchorId = null;
            state.ui.rightPanelView = RIGHT_PANEL_VIEW.INSTRUCTIONS;
            state.savedPlan.isSaved = true;
            applySelectionReconcile(state);
            if (state.savedPlan.testCaseId) {
                state.workflow.mode = WORKFLOW_MODE.READY_TO_REPLAY;
            } else {
                syncWorkflowAfterPlanChange(state);
            }
            setLeftPanelGroupSnapshot(state);
        },

        resetStudioNew: () => initialState,

        resetForNewTestCase: (state) => ({
            ...initialState,
            ui: {
                ...initialState.ui,
                selectedEnvironment: state.ui.selectedEnvironment,
                selectedAIModel: state.ui.selectedAIModel,
                selectedAIModelIsAuto: state.ui.selectedAIModelIsAuto,
            },
            mcpToolsList: state.mcpToolsList,
            mcpToolMap: state.mcpToolMap,
            fileSuggestions: state.fileSuggestions,
            fileDescriptions: state.fileDescriptions,
        }),
    },
});

export const {
    startAutomation,
    setInstructions,
    replacePlanInstructions,
    clearInstructionEditorText,
    resetInstructionTextFromBaseline,
    markInstructionsChanged,
    updateStepInPlan,
    loadPlanFromTestcase,
    setSavedPlanLoading,
    setValidationErrors,
    generationStarted,
    generationLogReceived,
    generationHierarchicalReceived,
    generationExecutionFlowReceived,
    generationScreenshotReceived,
    generationTestStepReceived,
    generationTokenUsageReceived,
    generationCompleted,
    generationFailed,
    generationStopped,
    markPlanUnsavedFromGeneration,
    replayStarted,
    replayStepStarted,
    replayStepCompleted,
    replayCompleted,
    replayFailed,
    replayStopped,
    replayLogReceived,
    planStructureChanged,
    saveStarted,
    saveFailed,
    planSaved,
    instructionsOnlySaved,
    selectStep,
    selectStepWithModifier,
    setSelectionAfterPaste,
    addStepAfter,
    deleteStepFromPlan,
    selectAdjacentStep,
    setStreamedInstructions,
    clearSelection,
    navigateToInstructions,
    navigateToExecution,
    setSelectedEnvironment,
    setSelectedAIModel,
    setMcpToolsList,
    setFileSuggestions,
    setHasEnteredInstructionsMode,
    importAutomationPlan,
    resetPlanToSnapshot,
    resetStudioNew,
    resetForNewTestCase,
} = automationStudioNewSlice.actions;

export default automationStudioNewSlice.reducer;

export { findStepById };
